import { useMessageSource } from 'src/i18n/useMessageSource'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
  ConfigurationTablePageSortSettings,
  ConfigurationGridDataSettings,
} from 'src/shared/constants/table-settings-constants'
import { Feature_Type_Config_Bool_Exp, Query_Root } from 'src/@types/graphql'
import { queryFeatureTypeConfigs } from 'src/screens/administration/configuration-management/features-configuration/feature-types/featureTypesConfigQueries'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { FeatureTypesConfigGrid } from 'src/screens/administration/configuration-management/features-configuration/feature-types/FeatureTypesConfigGrid'
import { Autocomplete, Box, Button, Grid2, Stack, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import { Option } from 'src/shared/form/control'
import { CONFIGURATION_STATUS, PROCESS } from 'src/shared/constants/constants'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  getConfigStatusOptions,
  getOptionsForFilter,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { getFeaturesConfigProcessOptions, getTranslationForFeatureProcess } from '../FeaturesConfigurationUtils'
import { deleteFeatureTypeConfig } from 'src/screens/administration/configuration-management/features-configuration/feature-types/FeatureTypesConfigGridUtils'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'

const FEATURE_TYPE_TABLE_NAME = 'FEATURE_TYPE'

type FeatureTypesFilters = SpecificConfigFilter<'FEATURE_TYPE'>

const defaultFeatureTypesTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
}

export const FeaturesConfigurationFeatureTypesPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  // filter options
  const featureTypesProcessOptions: Option[] = useMemo(
    () => getOptionsForFilter(getFeaturesConfigProcessOptions(getMessage)),
    [getMessage],
  )

  const featureTypesStatusOptions: Option[] = useMemo(
    () => getOptionsForFilter(getConfigStatusOptions(getMessage)),
    [getMessage],
  )

  const defaultFeatureTypesFiltersValues: FeatureTypesFilters = useMemo(
    () => ({
      featureTypesProcessFilter: featureTypesProcessOptions[0],
      featureTypesStatusFilter: featureTypesStatusOptions[0],
    }),
    [featureTypesProcessOptions, featureTypesStatusOptions],
  )

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    FEATURE_TYPE_TABLE_NAME,
    defaultFeatureTypesTableSettings,
  )
  const [featureTypesFilters, setFeatureTypesFilters] = useFilterSettingsFromLocalStorage(
    defaultFeatureTypesFiltersValues,
  )

  // delete modal state
  const [deleteModalLoading, setDeleteModalLoading] = useState(false)
  const [featureTypeToBeDeleted, setFeatureTypeToBeDeleted] = useState<number | null>(null)

  // table state
  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const featureTypesCondition: Feature_Type_Config_Bool_Exp = { _and: [] }
    if (featureTypesFilters.featureTypesStatusFilter.value != null) {
      const isActive = featureTypesFilters.featureTypesStatusFilter.value === CONFIGURATION_STATUS.ACTIVE
      featureTypesCondition._and?.push({ active: { _eq: isActive } })
    }
    if (featureTypesFilters.featureTypesProcessFilter.value != null) {
      const selectedProcess = featureTypesFilters.featureTypesProcessFilter.value as string
      featureTypesCondition._and?.push({ feature_configs: { processes: { _contains: selectedProcess } } })
    }

    const { data, error } = await urqlClient
      .query<
        {
          feature_type_config: Query_Root['feature_type_config']
          feature_type_config_aggregate: Query_Root['feature_type_config_aggregate']
        },
        {
          featureTypesCondition: Feature_Type_Config_Bool_Exp
          offset: number
          limit: number
        }
      >(queryFeatureTypeConfigs, {
        featureTypesCondition,
        offset,
        limit,
      })
      .toPromise()

    if (error || !data?.feature_type_config || !data.feature_type_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.feature_type_config.map((ftc) => {
      const allProcesses = [PROCESS.KAP, PROCESS.PF_KAP, PROCESS.PF_PGV]
      const allProcessesOfRelatedFeatureConfigs = new Set(ftc.feature_configs.flatMap((fc) => fc.processes))

      const finalProcessesInType = allProcesses.filter((process) => allProcessesOfRelatedFeatureConfigs.has(process))

      return {
        id: ftc.id,
        name: ftc.feature_type_name_locale,
        status: ftc.active,
        allocation: finalProcessesInType,
        is_deletable: !!ftc.feature_type_deletability?.is_deletable,
      }
    })
    const fetchedRowsCount = data.feature_type_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [tableSettings.paginationModel, featureTypesFilters, urqlClient])

  const initRows = useCallback(() => {
    setGridDataSettings((prevGridData) => ({
      ...prevGridData,
      gridLoading: true,
    }))

    fetchRows()
      .then((res) => {
        setGridDataSettings({
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
          gridLoading: false,
        })
      })
      .catch((_) => {
        notificationService.operationFailed()
        setGridDataSettings((prevDataSettings) => ({
          ...prevDataSettings,
          gridLoading: false,
        }))
      })
  }, [fetchRows, notificationService])

  // filter handlers
  const onFeatureTypesProcessChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })
      setFeatureTypesFilters({
        ...featureTypesFilters,
        featureTypesProcessFilter: newValue ?? defaultFeatureTypesFiltersValues.featureTypesProcessFilter,
      })
    },
    [
      tableSettings,
      featureTypesFilters,
      defaultFeatureTypesFiltersValues.featureTypesProcessFilter,
      setTableSettings,
      setFeatureTypesFilters,
    ],
  )

  const onFeatureTypesStatusChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })
      setFeatureTypesFilters({
        ...featureTypesFilters,
        featureTypesStatusFilter: newValue ?? defaultFeatureTypesFiltersValues.featureTypesStatusFilter,
      })
    },
    [
      tableSettings,
      featureTypesFilters,
      defaultFeatureTypesFiltersValues.featureTypesStatusFilter,
      setTableSettings,
      setFeatureTypesFilters,
    ],
  )

  const handleResetFilters = useCallback(() => {
    setFeatureTypesFilters(defaultFeatureTypesFiltersValues)
    setTableSettings({
      ...tableSettings,
      paginationModel: { ...tableSettings.paginationModel, page: 0 },
    })
  }, [defaultFeatureTypesFiltersValues, tableSettings, setFeatureTypesFilters, setTableSettings])

  // delete modal handlers
  const handleCancelDelete = useCallback(() => {
    setFeatureTypeToBeDeleted(null)
  }, [])

  const handleDeleteConfirmed = useCallback(() => {
    if (!featureTypeToBeDeleted) {
      notificationService.operationFailed()
      return
    }

    setDeleteModalLoading(true)
    deleteFeatureTypeConfig(urqlClient)(featureTypeToBeDeleted)
      .then(() => {
        notificationService.deleteSuccessful()
        initRows()
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setDeleteModalLoading(false)
        setFeatureTypeToBeDeleted(null)
      })
  }, [featureTypeToBeDeleted, initRows, notificationService, urqlClient])

  useEffect(() => {
    initRows()
  }, [initRows])

  return (
    <>
      <ScreenLayout
        title={`${getMessage('label.configuration.features.feature.types')} ${getMessage('label.configuration')}`}
      >
        <PageLayout>
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 1,
              padding: 2,
              marginBottom: 2,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="body1" color="text.secondary" fontWeight="bold">
                {getMessage('label.grid.filter')}
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={6}>
                  <Autocomplete
                    options={featureTypesProcessOptions}
                    value={featureTypesFilters.featureTypesProcessFilter}
                    onChange={(_, newVal) => onFeatureTypesProcessChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForFeatureProcess(getMessage, opt.value)}
                    renderInput={(params) => <TextField {...params} label={getMessage('label.process')} />}
                  />
                </Grid2>
                <Grid2 size={6}>
                  <Autocomplete
                    options={featureTypesStatusOptions}
                    value={featureTypesFilters.featureTypesStatusFilter}
                    onChange={(_, newVal) => onFeatureTypesStatusChange(newVal)}
                    getOptionLabel={(opt) => getTranslationForStatus(getMessage, opt.value)}
                    renderInput={(params) => <TextField {...params} label={getMessage('label.status')} />}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container>
                <Grid2 size={4}>
                  <Button variant="text" startIcon={<FilterAltOffOutlinedIcon />} onClick={handleResetFilters}>
                    {getMessage('label.configuration.criteria.group.grid.filter.reset.button')}
                  </Button>
                </Grid2>
              </Grid2>
            </Stack>
          </Box>
          <FeatureTypesConfigGrid
            dataSettings={gridDataSettings}
            tableSettings={tableSettings}
            setTableSettings={setTableSettings}
            setFeatureTypeToBeDeleted={setFeatureTypeToBeDeleted}
          />
        </PageLayout>
      </ScreenLayout>

      <SecondaryConfirmationModalDialog
        open={!!featureTypeToBeDeleted}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteConfirmed}
        loading={deleteModalLoading}
        buttonColor="error"
      >
        <Box paddingRight={1}>
          <Typography variant="subtitle1">
            {getMessage('label.configuration.features.feature.type.grid.delete.confirm.body')}
          </Typography>
        </Box>
      </SecondaryConfirmationModalDialog>
    </>
  )
}
